import { useTranslate } from '../../application/i18n/LocaleProvider';
import { specLabelKey } from '../../domain/cars/carProperties';
import { duelSpec, leadKey, questionKey } from '../../domain/game/duelSpecs';
import { duelDirectionIcon, duelSubjectIcon } from './duelSubjects';

/**
 * The question, ASSEMBLED a line at a time (SPEC-007 D-36, D-37).
 *
 * *Which car* — *has more* — **⚡ POWER**. Three parts, each landing a beat after
 * the one before it, and the staging is the point rather than the decoration. A
 * question that appears whole is read; a question that assembles is watched, and
 * the beat before the subject lands is the moment the reader is deciding what
 * they are about to be asked. It is a quiz show's trick and it costs nothing but
 * the order things appear in.
 *
 * THE TWO LEAD LINES ARE WORDS AND THE SUBJECT IS A PICTURE, which is a split
 * rather than an inconsistency. Those two lines are the same four words on every
 * question in the game — a reader learns them once and then never reads them
 * again — where the subject changes every round and is the only thing that has
 * to be understood at a glance. So the words carry the part that is constant and
 * the picture carries the part that is not.
 *
 * THE ARROW STAYS, on the line that is not constant. More against less is the
 * half of the question a reader has to get right to have any chance at all, and
 * a nine-year-old or anybody with no English would otherwise be left guessing
 * which way round it ran. It sits ON the line rather than in a plate of its own,
 * because it is a mark on a sentence rather than a part of the question in its
 * own right.
 *
 * Each line is its OWN message (`duel.lead.which`, `duel.lead.higher`) rather
 * than one sentence sliced in two here. That is the most that can be done for a
 * question the design builds a line at a time: a translator writes each line
 * whole, and the day a language cannot be broken at this seam the fix is copy
 * that reads as two lines rather than a component that reassembles fragments.
 *
 * The full sentence is still here and it is the HEADING — clipped, so the pair
 * of cards below is labelled by a real question rather than by two fragments and
 * a label. What is drawn is a DRAWING of it, so it is `aria-hidden`: a screen
 * reader given both would ask twice.
 */
export default function DuelQuestion({ spec, revealed, id }) {
    const t = useTranslate();
    const SubjectIcon = duelSubjectIcon(spec);
    const DirectionIcon = duelDirectionIcon(duelSpec(spec)?.wins);

    return (
        <header className="duel-question">
            <h2 className="visually-hidden" id={id}>{t(questionKey(spec))}</h2>

            <div className="duel-question__strip" aria-hidden="true">
                <p className="duel-question__part duel-question__part--which">
                    {t('duel.lead.which')}
                </p>

                <p className="duel-question__part duel-question__part--direction">
                    <DirectionIcon className="duel-question__arrow" size={18} />
                    <span className="duel-question__sentence">{t(leadKey(spec))}</span>
                </p>

                <p className="duel-question__part duel-question__part--subject">
                    {SubjectIcon ? <SubjectIcon className="duel-question__glyph" size={32} /> : null}
                    <span className="duel-question__word">{t(specLabelKey(spec))}</span>
                </p>
            </div>

            {/* An instruction, and an instruction for something already done is
                furniture — so it goes the moment there is an answer. */}
            {revealed ? null : <p className="duel-question__prompt">{t('duel.choose')}</p>}
        </header>
    );
}
